import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '../../services/toast-service';
import { WebService } from '../../services/web-service';
import { BaseTask } from '../../shared/base-task-directive';
import { WebIngestionDto } from '../../shared/interfaces';
import { WebTableComponent } from '../../shared/web-table-component/web-table-component';
import { WebIngestionDuplicatesModal } from './web-ingestion-duplicates-modal/web-ingestion-duplicates-modal';
import { WebIngestionModal } from './web-ingestion-modal/web-ingestion-modal';

@Component({
  selector: 'app-web-sites',
  standalone: true,
  imports: [CommonModule, WebTableComponent, NgbDropdownModule],
  templateUrl: './web-sites.html',
  styleUrl: './web-sites.scss',
})
export class WebSites extends BaseTask {
  indexName = input<string>('');
  targetIndex = input<string>('');

  effectiveIndex = computed(() => this.indexName() || this.targetIndex() || '');

  public readonly webService = inject(WebService);
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);
  
  websitesData = signal<any[]>([]);
  activeIndexName = signal<string | null>(null);

  constructor() {
    super();
    effect(() => {
      const idx = this.effectiveIndex();
      if (idx) {
        this.handleIndexChanged(idx);
      }
    });
  }

  onTaskEventTriggered() {
    const index = this.activeIndexName();
    if (index) {
      this.getPendingTask(index, 'WEB_CRAWL');
    }
  }

  loadData() {
    this.isLoading.set(true);
    this.webService.getWebsites().subscribe({
      next: (data) => {
        this.websitesData.set(data);
        this.isLoading.set(false);
        const idx = this.effectiveIndex();
        if (idx) {
          this.handleIndexChanged(idx);
        }
      },
      error: (err) => {
        console.error('Errore nel recupero dei siti web:', err);
        this.isLoading.set(false);
      }
    });
  }

  handleIndexChanged(indexName: string | null) {
    this.activeIndexName.set(indexName);
    if (indexName) {
      this.getPendingTask(indexName, 'WEB_CRAWL');
    }
  }

  openWebIngestionModal() {
    const currentIndex = this.activeIndexName();
    if (!currentIndex) return;

    const modalRef = this.modalService.open(WebIngestionModal, { 
      centered: true, 
      windowClass: 'agentic-custom-modal' 
    });

    modalRef.componentInstance.indexName = currentIndex;

    modalRef.result.then((result: { url: string; maxPages?: number; generatePreview?: boolean } | string) => {
      if (result) {
        const urlToCrawl = typeof result === 'string' ? result : result.url;
        const maxPages = typeof result === 'object' ? result.maxPages : undefined;
        const generatePreview = typeof result === 'object' ? result.generatePreview : undefined;
        // Prepariamo il payload iniziale
        const payload: WebIngestionDto = {
          urls: [urlToCrawl],
          indexName: currentIndex,
          type: 'auto' as const,
          maxPages,
          generatePreview,
        };
        this.executeIngestion(payload);
      }
    }).catch(() => {});
  }

  // --- FASE 2: ESECUZIONE CHIAMATA ---
  private executeIngestion(payload: WebIngestionDto) {
    this.isLoading.set(true);

    this.webService.crawlWebsite(payload).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        
        // GESTIONE DUPLICATI (Conflitti di URL)
        if (res.status === 'DUPLICATES_FOUND' && res.conflicts) {
          this.handleDuplicatesModal(payload, res);
        } else {
          this.toastService.show('Scansione web avviata con successo', 'success');
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.toastService.show('Errore durante l\'avvio della scansione', 'error');
      }
    });
  }

  // --- FASE 3: GESTIONE MODALE CONFLITTI ---
  private handleDuplicatesModal(originalPayload: WebIngestionDto, res: any) {
    const modalRef = this.modalService.open(WebIngestionDuplicatesModal, { 
      centered: true, 
      windowClass: 'agentic-custom-modal' 
    });

    modalRef.componentInstance.duplicatesList = res.conflicts;
    modalRef.componentInstance.conflictType = res.conflictType; 
    modalRef.componentInstance.allDuplicates = true; // Lato web inseriamo un URL alla volta

    modalRef.result.then((userChoice: 'replace' | 'cancel') => {
      if (!userChoice || userChoice === 'cancel') return; 

      if (userChoice === 'replace') {
        this.executeIngestion({ ...originalPayload, action: 'replace' });
      }
    }).catch(() => {
      // L'utente ha cliccato fuori o sulla X
    });
  }
}